import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useForm, useWatch } from 'react-hook-form';
import type { SlotContents } from '@amplience/content-types/typings/s-pdp-layout';
import { yupResolver } from '@hookform/resolvers/yup';
import { GiftCardMessageSchema } from '@shared/validation/src/b2c';
import { CMS_SLOTS_DELIVERY_KEYS, CmsSlot } from '@storefront/lib-global';
import type { Maybe, ProductReference } from '@storefront/lib-global/codegen/types';
import { createGlobalAmplienceComponent } from '@storefront/lib-global/globalComponents/globalAmplienceComponents.utils';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import { renderAmplienceSlotComponents } from '@storefront/lib-global/utils/amplienceUtils';
import type { SupportedGiftCardTypes } from '../../../types';
import { GiftCardMessageForm } from '../GiftCardMessageForm';
import ProductName from '../ProductName/ProductName';
import AddToBag from '../addToBag/AddToBag';
import GiftCardOccasionSelector from '../giftCardOccasionSelector/GiftCardOccasionSelector';
import { createPDPAmplienceComponent } from '../pdpAmplienceComponents.utils';
import PriceDenominations from '../priceDenominations/PriceDenominations';
import {
  GiftCardInfoContainer,
  GiftCardTypeContainer,
  GiftCardTypeSelectorButton,
  GiftCardTypeSelectorContainer,
} from './GiftCardInfo.styles';

export type GiftCardInfoProps = {
  slotContent: SlotContents | null;
  name: string;
  giftCardType: string;
  references?: Maybe<Array<ProductReference>>;
  handlebarComponents: HandlebarTemplateProps[] | null;
  supportedTypes: SupportedGiftCardTypes;
  shouldShowDesktopH1?: boolean;
};

export type GiftCardFormValues = {
  to: string;
  from: string;
  email: string;
  message: string;
};

function createGiftCardLink(
  currentGiftCardType: string,
  buttonType: string,
  referenceLink?: Maybe<Array<ProductReference>>,
) {
  if (currentGiftCardType !== buttonType && referenceLink && referenceLink[0]?.targetProduct?.key) {
    return `/${referenceLink[0]?.targetProduct?.key}-p`;
  }
  return '';
}

export function GiftCardInfo({
  slotContent,
  name,
  giftCardType,
  references,
  handlebarComponents,
  supportedTypes,
  shouldShowDesktopH1,
}: GiftCardInfoProps) {
  const { locale } = useRouter();

  const [activeGiftCardType, setActiveGiftCardType] = useState(giftCardType);

  useEffect(() => {
    setActiveGiftCardType(giftCardType);
  }, [giftCardType]);

  const selectGiftCardType = useCallback(
    (type: string) => () => {
      setActiveGiftCardType(type);
    },
    [],
  );

  const { t } = useTranslation(['lib-products-common']);

  const giftCardHref = createGiftCardLink(activeGiftCardType, 'giftCard', references);
  const eGiftCardHref = createGiftCardLink(activeGiftCardType, 'egiftCard', references);

  const {
    register,
    control,
    trigger,
    formState: { errors: formError, isValid },
  } = useForm<GiftCardFormValues>({
    mode: 'onChange',
    resolver: yupResolver(GiftCardMessageSchema(giftCardType === 'egiftCard')),
  });

  const [to, email, from, message] = useWatch({
    control,
    name: ['to', 'email', 'from', 'message'],
  });

  const isEGiftCard = giftCardType === 'egiftCard';
  const showGiftCardSelector: boolean = supportedTypes.physical && supportedTypes.digital;

  return (
    <GiftCardInfoContainer>
      {shouldShowDesktopH1 && (
        <>
          <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_ABOVE_NAME_BANNER} />
          <ProductName name={name} />
        </>
      )}

      <GiftCardTypeContainer>
        {showGiftCardSelector ? (
          <div>
            <span>{t('pdp.giftcard.type')}</span>
            <GiftCardTypeSelectorContainer>
              <Link href={giftCardHref}>
                <GiftCardTypeSelectorButton
                  data-testid="giftCardTypeButton"
                  onClick={selectGiftCardType('giftCard')}
                  $isActive={activeGiftCardType === 'giftCard'}
                  disabled={activeGiftCardType === 'giftCard'}
                >
                  {t('pdp.giftcard')}
                </GiftCardTypeSelectorButton>
              </Link>
              <Link href={eGiftCardHref}>
                <GiftCardTypeSelectorButton
                  data-testid="egiftCardTypeButton"
                  onClick={selectGiftCardType('egiftCard')}
                  $isActive={activeGiftCardType === 'egiftCard'}
                  disabled={activeGiftCardType === 'egiftCard'}
                >
                  {t('pdp.egiftcard')}
                </GiftCardTypeSelectorButton>
              </Link>
            </GiftCardTypeSelectorContainer>
          </div>
        ) : null}
        <GiftCardOccasionSelector />
        <PriceDenominations />
        <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_BELOW_PRICE_BANNER} />
      </GiftCardTypeContainer>
      <GiftCardMessageForm
        to={to}
        from={from}
        email={isEGiftCard ? email : undefined}
        isEGiftCard={isEGiftCard}
        formError={formError}
        register={register}
        message={message}
      />
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_ABOVE_CTA_BANNER} />
      <AddToBag
        isGiftCard
        to={to}
        email={isEGiftCard ? email : undefined}
        from={from}
        message={message}
        data-testid="giftCardMessageForm"
        trigger={trigger}
        isValid={isValid}
      />
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_BELOW_CTA_BANNER} />
      {slotContent &&
        renderAmplienceSlotComponents(
          slotContent.belowATBContent as [],
          {
            ...createGlobalAmplienceComponent(slotContent.belowATBContent as []),
            ...createPDPAmplienceComponent(slotContent.belowATBContent as []),
          },
          handlebarComponents,
          locale,
        )}
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_BELOW_FEATURES_BANNER} />
    </GiftCardInfoContainer>
  );
}
